import { ColDef, GetMainMenuItemsParams } from 'ag-grid-enterprise';
import { ColumnDef } from './ts-table.model';

export namespace TsTableUtils {
  /**
   * Private list of field names that should be displayed without number formatting.
   * These fields will be displayed as plain strings without any locale-specific formatting.
   * @private
   */
  const fieldsBlacklist = ['id'] as const;

  /**
   * List of column fields that should have their header menu suppressed.
   * These are typically system columns that don't need user interaction.
   */
  const SUPPRESSED_COLUMN_FIELDS = ['checkbox', 'openDetails', 'actions'] as const;

  /**
   * Formats a number according to specific rules based on the field definition.
   *
   * @param rawValue - The raw value to be formatted (can be number, string, null, or undefined)
   * @param fieldDef - The column definition containing field metadata
   * @returns Formatted string representation of the number
   *
   * Rules applied:
   * 1. If the value is null, undefined or NaN, returns an empty string
   * 2. If the field name is in the blacklist (e.g. 'id'), returns the value as a plain string
   * 3. Otherwise, formats the number using US locale formatting (e.g. 1000 -> "1,000")
   */
  export const formatNumberWithFieldRules = (
    rawValue: number | string | null | undefined,
    fieldDef: ColumnDef,
  ): string => {
    const value = typeof rawValue === 'number' ? rawValue : Number(rawValue);

    if (value === null || value === undefined || Number.isNaN(value)) {
      return '';
    }

    return (fieldsBlacklist as readonly string[]).includes(fieldDef.name)
      ? value.toString()
      : value.toLocaleString('en-US');
  };

  /**
   * Determines if a column should have its header menu suppressed.
   *
   * @param columnField - The field name of the column
   * @returns true if the column menu should be suppressed, false otherwise
   */
  export const shouldSuppressMenuItems = (columnField: string): boolean => {
    return SUPPRESSED_COLUMN_FIELDS.includes(columnField as any);
  };

  /**
   * Determines if sort column menu options should be suppressed (hidden) for a column.
   * Sort options should only be hidden when exclude_in_nested_filter is true.
   *
   * @param colDef - The column definition from ag-grid.
   * @returns true if sort column menu options should be suppressed (hidden), false if they should be shown.
   */
  const shouldSuppressSortMenuOptions = (colDef: ColDef, nestedFiltersEnabled: boolean): boolean => {
    // Determine the source object: use fieldDef if it exists (for 'Name' or 'Title' columns generated separately), otherwise use context directly.
    const source = colDef.context?.fieldDef || colDef.context;

    if (!source) {
      return true;
    }

    const isNestedField = source.name?.includes('.');

    if (isNestedField) {
      return true;
    }

    // When nestedFilters is enabled, use exclude_in_nested_filter to determine sortability.
    if (nestedFiltersEnabled) {
      // If exclude_in_nested_filter is true, suppress sorting
      if (source.meta?.filtering?.exclude_in_nested_filter === true) {
        return true;
      }
      // If exclude_in_nested_filter is false or undefined, allow sorting.
      return false;
    }

    // When nestedFilters is disabled, fields with exclude_in_nested_filter: true can still be sorted
    if (source.meta?.filtering?.exclude_in_nested_filter === true) {
      return false;
    }

    // For other fields, check sortability flags
    if (source.tsTableOptions?.sortable !== undefined) {
      return !source.tsTableOptions.sortable;
    }

    return !source.meta?.sorting;
  };

  /**
   * Builds the main menu items array for ag-grid column header menu.
   * Conditionally includes sort options based on field metadata.
   *
   * @param params - The GetMainMenuItemsParams from ag-grid.
   * @param additionalMenuItems - Optional array of additional menu items to include (e.g., custom actions).
   * @returns Array of menu items, or empty array if the column menu should be suppressed.
   */
  export const getMainMenuItems = (nestedFiltersEnabled: boolean, params: GetMainMenuItemsParams, additionalMenuItems: any[] = []): any[] => {
    const colDef = params.column.getColDef();
    const fieldName = colDef.field;

    // If the column menu should be suppressed entirely, return empty array
    if (shouldSuppressMenuItems(fieldName)) {
      return [];
    }

    const menuItems: any[] = [];

    // Only add sort options if they should not be suppressed
    const shouldSuppressSort = shouldSuppressSortMenuOptions(colDef, nestedFiltersEnabled);
    if (!shouldSuppressSort) {
      menuItems.push('sortAscending', 'sortDescending');
    }

    // Add pin submenu
    menuItems.push('pinSubMenu');

    // Add any additional menu items
    if (additionalMenuItems.length > 0) {
      menuItems.push(...additionalMenuItems);
    }

    return menuItems;
  };

  export const isColumnSortable = (fieldDef: ColumnDef, nestedFiltersEnabled: boolean): boolean => {
    const isNestedField = fieldDef.name?.includes('.');

    if (isNestedField) {
      return false;
    }

    // When nestedFilters is enabled, use exclude_in_nested_filter to determine sortability
    if (nestedFiltersEnabled) {
      // If exclude_in_nested_filter is true, suppress sorting.
      if (fieldDef.meta?.filtering?.exclude_in_nested_filter === true) {
        return false;
      }
      // If exclude_in_nested_filter is false or undefined, allow sorting
      return true;
    }

    // When nestedFilters is disabled, fields with exclude_in_nested_filter: true can still be sorted.
    if (fieldDef.meta?.filtering?.exclude_in_nested_filter === true) {
      return true;
    }

    // For other fields, check sortability flags.
    if (fieldDef.tsTableOptions?.sortable !== undefined) {
      return fieldDef.tsTableOptions.sortable;
    }

    return !!fieldDef.meta?.sorting;
  }
}
